/**
 * Client-side photo compression — design v1.1 §4.1: a 4–8 MB phone photo becomes
 * ~200–400 KB *before* it queues, which is what makes "5 photos < 15 s on 4G"
 * arithmetic rather than hope.
 *
 * `imageOrientation: 'from-image'` applies EXIF rotation at decode, the same fact the
 * thumbnail job handles server-side with sharp's `.rotate()` — both ends agree portrait
 * photos are portrait.
 *
 * HONESTY NOTE ON TESTS: jsdom has no image decoder and no canvas, so this module has
 * no unit test — it is exercised by the device field test (Phase 1 step 8), and its
 * callers treat it as fallible: a compression failure falls back to uploading the
 * original file, ledgered, because a 6 MB upload that works beats a lost photo.
 */

const LONG_EDGE = 1600;
const QUALITY = 0.8;

/**
 * `longEdge` overrides the 1600px default for a picture that is only ever SHOWN small.
 *
 * ⚠️ ADDED FOR THE CHARACTER PLATE (0.1.400, device report: "it takes a long time for the
 * character card's photo tile to load in … I think it's loading the full-size image instead of a
 * thumbnail"). It was — and there is no thumbnail to load, because `thumbnails.generate` is keyed
 * on `item_photos` rows and a plate has none. Rather than widen that job to a second subject, the
 * plate is simply STORED at the size it is displayed: a 48px circle has no use for 1600px, and
 * one small object beats two objects and a worker.
 *
 * Garment photos keep the default and must: they are opened full-screen in the viewer.
 *
 * 0.1.528: the plate is now 1200px (maintainer, 3 Oct 2026 — it is also shown as a big circle on
 * the character's own screen, "in full quality"), and what arrives here is the CROPPED square from
 * `components/image-crop.tsx`, a Blob rather than a picked File — hence `Blob`, which a File is.
 */
export async function compressImage(file: Blob, longEdge: number = LONG_EDGE): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, longEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d canvas context');
    context.drawImage(bitmap, 0, 0, width, height);

    const compressed = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', QUALITY);
    });
    if (!compressed) throw new Error('canvas.toBlob returned null');
    // A "compression" that grew the file (already-tiny input) ships the original.
    return compressed.size < file.size ? compressed : file;
  } finally {
    bitmap.close();
  }
}
